declare global {
  interface Window {
    /**
     * The analytics shim defined inline in `index.html`. Optional in the type
     * on purpose: it is a hand-written script tag, not a module the bundler
     * guarantees, and every caller here has to survive its absence.
     */
    dp?: {
      track?: (event: string, props?: Record<string, unknown>) => void
    }
  }
}

/**
 * Record a product event, or do nothing.
 *
 * `window.dp.track` already swallows its own errors and already no-ops until
 * the cookie bar grants consent and off the live host (`DP_HOSTS` in
 * `index.html`). This wrapper adds the one case that block cannot cover — the
 * shim not being there at all — and gives React components a typed way in,
 * rather than each one reaching for `(window as any).dp`.
 *
 * **Every count that comes out of this is a floor, never a total**, because a
 * visitor who declines cookies is not counted. That is fine for the events
 * measured this way: they are all "does this happen at all, and where", and a
 * floor above zero answers that.
 */
export function track(event: string, props?: Record<string, unknown>): void {
  try {
    window.dp?.track?.(event, props)
  } catch {
    /* analytics must never be able to break a page */
  }
}

/**
 * The referrer's PATH, and only when the referrer is this site.
 *
 * Pure and both-arguments-in so it can be driven with hostile input by
 * `pnpm gate` — the interesting cases here are all ones a browser will not
 * hand you on request.
 *
 * **Never the full referrer URL.** A referrer carries whatever query string
 * the other site put on it: a search term, a session token, an email address
 * in a tracking parameter. None of that has any business reaching a
 * third-party processor because somebody followed a dead link, and there is
 * still no signed DPA with PostHog. The external case loses nothing worth
 * having — PostHog sets `$referring_domain` on every event by itself, which
 * is the part that answers "LinkedIn, Google, or direct".
 *
 * Origins are compared after `new URL()` has parsed them, never as a string
 * match on the hostname: `https://www.digitopia.ai.evil.com/x` contains our
 * hostname and is not ours.
 */
export function sameSitePath(referrer: string, origin: string): string | null {
  try {
    if (!referrer) return null
    const ref = new URL(referrer)
    return ref.origin === origin ? ref.pathname : null
  } catch {
    // A malformed referrer is not a path. Never an exception: this runs
    // inside a render effect on a page that already went wrong once.
    return null
  }
}
